/**
 * Douglas–Peucker line simplification to a point budget — the client-side counterpart of
 * the backend's RideGeometry simplifier. Used to thin a route's geometry until it fits a
 * transport cap (e.g. sharing an unsaved route's line through the SignalR hub, which has
 * a receive-size limit): start at a metre-scale tolerance and double it until the result
 * fits. Endpoints always survive; the input is returned as-is when already small enough.
 *
 * Coordinates are [lon, lat] pairs (GeoJSON order — what routes and GPX parses carry).
 * Distances use a locally-scaled equirectangular projection: exact enough for tolerances
 * of metres over route-sized extents, and cheap enough to run on a phone mid-ride.
 */
export function simplifyToPointBudget(
  coordinates: [number, number][],
  maxPoints: number,
  baseToleranceMeters = 1,
): [number, number][] {
  if (coordinates.length <= maxPoints) return coordinates;
  let tolerance = baseToleranceMeters;
  let result = coordinates;
  // Doubling from 1 m crosses any realistic route's fidelity range in ~20 steps; the
  // hard stop only guards against degenerate input (all points identical, huge budget=2).
  for (let i = 0; i < 32 && result.length > maxPoints; i++) {
    result = douglasPeucker(coordinates, tolerance);
    tolerance *= 2;
  }
  return result;
}

function douglasPeucker(coords: [number, number][], toleranceMeters: number): [number, number][] {
  if (coords.length <= 2) return coords;

  // Metres per degree at the line's mid-latitude — good enough over route extents.
  const midLat = (coords[0][1] + coords[coords.length - 1][1]) / 2;
  const mPerDegLat = 111_320;
  const mPerDegLon = mPerDegLat * Math.cos((midLat * Math.PI) / 180);

  const keep = new Uint8Array(coords.length);
  keep[0] = 1;
  keep[coords.length - 1] = 1;

  // Iterative stack — GPX tracks can be deep enough to overflow a recursive version.
  const stack: [number, number][] = [[0, coords.length - 1]];
  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    const ax = coords[first][0] * mPerDegLon;
    const ay = coords[first][1] * mPerDegLat;
    const bx = coords[last][0] * mPerDegLon;
    const by = coords[last][1] * mPerDegLat;
    const dx = bx - ax;
    const dy = by - ay;
    const lengthSq = dx * dx + dy * dy;

    let maxDist = 0;
    let maxIndex = -1;
    for (let i = first + 1; i < last; i++) {
      const px = coords[i][0] * mPerDegLon;
      const py = coords[i][1] * mPerDegLat;
      let dist: number;
      if (lengthSq === 0) {
        dist = Math.hypot(px - ax, py - ay);
      } else {
        const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
        dist = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
      }
      if (dist > maxDist) {
        maxDist = dist;
        maxIndex = i;
      }
    }

    if (maxIndex > 0 && maxDist > toleranceMeters) {
      keep[maxIndex] = 1;
      stack.push([first, maxIndex], [maxIndex, last]);
    }
  }

  const result: [number, number][] = [];
  for (let i = 0; i < coords.length; i++) {
    if (keep[i]) result.push(coords[i]);
  }
  return result;
}
